import { Lock, Pencil, Plus, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface FieldMapping {
  id: string;
  target: string;
  hubspotField: string;
  localColumn: string;
  columnType: string;
  isSystem: boolean;
}

interface MappableColumn {
  name: string;
  type: string;
}

interface HubSpotField {
  name: string;
  label: string;
  type: string;
}

interface PipelineMappingsSectionProps {
  initialMappings: FieldMapping[];
  mappableColumns: { pipeline: MappableColumn[]; stage: MappableColumn[] };
  hubspotFields: { pipeline: HubSpotField[]; stage: HubSpotField[] };
}

export function PipelineMappingsSection({
  initialMappings,
  mappableColumns,
  hubspotFields,
}: PipelineMappingsSectionProps) {
  const [mappings, setMappings] = useState<FieldMapping[]>(initialMappings);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<FieldMapping | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FieldMapping | null>(null);

  // Form state
  const [target, setTarget] = useState<"pipeline" | "stage">("pipeline");
  const [hubspotField, setHubspotField] = useState("");
  const [localColumn, setLocalColumn] = useState("");
  const [saving, setSaving] = useState(false);

  const pipelineMappings = mappings.filter((m) => m.target === "pipeline");
  const stageMappings = mappings.filter((m) => m.target === "stage");

  const currentMappings = target === "pipeline" ? pipelineMappings : stageMappings;
  const mappedColumns = new Set(currentMappings.map((m) => m.localColumn));
  const mappedFields = new Set(currentMappings.map((m) => m.hubspotField));

  const availableColumns = (
    target === "pipeline" ? mappableColumns.pipeline : mappableColumns.stage
  ).filter((col) => !mappedColumns.has(col.name) || col.name === editingMapping?.localColumn);

  const availableFields = (
    target === "pipeline" ? hubspotFields.pipeline : hubspotFields.stage
  ).filter((f) => !mappedFields.has(f.name) || f.name === editingMapping?.hubspotField);

  const selectedColumnType = useMemo(() => {
    const cols = target === "pipeline" ? mappableColumns.pipeline : mappableColumns.stage;
    return cols.find((c) => c.name === localColumn)?.type ?? "—";
  }, [target, localColumn, mappableColumns]);

  // Build lookup for display
  const fieldLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of hubspotFields.pipeline) map.set(`pipeline:${f.name}`, f.label);
    for (const f of hubspotFields.stage) map.set(`stage:${f.name}`, f.label);
    return map;
  }, [hubspotFields]);

  const refreshMappings = useCallback(async () => {
    const res = await fetch("/api/pipelines/mappings");
    const data = await res.json();
    setMappings(data.mappings ?? []);
  }, []);

  function openAddDialog() {
    setEditingMapping(null);
    setTarget("pipeline");
    setHubspotField("");
    setLocalColumn("");
    setDialogOpen(true);
  }

  function openEditDialog(mapping: FieldMapping) {
    setEditingMapping(mapping);
    setTarget(mapping.target as "pipeline" | "stage");
    setHubspotField(mapping.hubspotField);
    setLocalColumn(mapping.localColumn);
    setDialogOpen(true);
  }

  async function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!hubspotField || !localColumn) {
      toast.error("Both fields are required");
      return;
    }
    setSaving(true);
    try {
      const isEdit = editingMapping !== null;
      const url = isEdit
        ? `/api/pipelines/mappings/${editingMapping.id}`
        : "/api/pipelines/mappings";
      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target, hubspotField, localColumn }),
      });

      if (!res.ok) {
        const err = await res.json();
        toast.error(err.error || "Failed to save mapping");
        return;
      }

      await refreshMappings();
      setDialogOpen(false);
      toast.success(isEdit ? "Mapping updated" : "Mapping added");
    } catch {
      toast.error("Failed to save mapping");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`/api/pipelines/mappings/${deleteTarget.id}`, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.error || "Failed to delete mapping");
        return;
      }
      await refreshMappings();
      setDeleteTarget(null);
      toast.success("Mapping deleted");
    } catch {
      toast.error("Failed to delete mapping");
    }
  }

  function renderMappingTable(title: string, items: FieldMapping[], targetType: string) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-sm font-medium">{title} Mappings</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              {items.length} fields mapped from HubSpot {targetType}s
            </p>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">No {targetType} mappings configured.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="text-xs uppercase tracking-wider">HubSpot Field</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Local Column</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((mapping) => (
                  <TableRow key={mapping.id}>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs w-fit">
                          {mapping.hubspotField}
                        </code>
                        {fieldLabelMap.get(`${mapping.target}:${mapping.hubspotField}`) && (
                          <span className="text-xs text-muted-foreground">
                            {fieldLabelMap.get(`${mapping.target}:${mapping.hubspotField}`)}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                          {mapping.localColumn}
                        </code>
                        {mapping.isSystem && (
                          <Lock className="h-3.5 w-3.5 text-muted-foreground/60" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          mapping.columnType === "real" || mapping.columnType === "integer"
                            ? "secondary"
                            : "outline"
                        }
                      >
                        {mapping.columnType}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => openEditDialog(mapping)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          <span className="sr-only">Edit</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => setDeleteTarget(mapping)}
                          disabled={mapping.isSystem}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span className="sr-only">Delete</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Field Mappings</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {mappings.length} total mappings across pipelines and stages
          </p>
        </div>
        <Button size="sm" onClick={openAddDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Add Mapping
        </Button>
      </div>

      {renderMappingTable("Pipeline", pipelineMappings, "pipeline")}
      {renderMappingTable("Stage", stageMappings, "stage")}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingMapping ? "Edit Mapping" : "Add Mapping"}</DialogTitle>
            <DialogDescription>
              Map a HubSpot pipeline field to a local database column.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            {/* Target selector */}
            <div className="space-y-2">
              <Label>Target Table</Label>
              <Select
                value={target}
                onValueChange={(v) => {
                  setTarget(v as "pipeline" | "stage");
                  setHubspotField("");
                  setLocalColumn("");
                }}
                disabled={editingMapping !== null}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pipeline">Pipeline</SelectItem>
                  <SelectItem value="stage">Stage</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* HubSpot Field */}
            <div className="space-y-2">
              <Label>HubSpot Field</Label>
              <Select value={hubspotField} onValueChange={setHubspotField}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a field" />
                </SelectTrigger>
                <SelectContent>
                  {availableFields.map((f) => (
                    <SelectItem key={f.name} value={f.name}>
                      {f.label}
                      <span className="ml-2 text-muted-foreground">({f.name})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Local Column */}
            <div className="space-y-2">
              <Label>Local Column</Label>
              <Select value={localColumn} onValueChange={setLocalColumn}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a column" />
                </SelectTrigger>
                <SelectContent>
                  {availableColumns.map((col) => (
                    <SelectItem key={col.name} value={col.name}>
                      {col.name}
                      <span className="ml-2 text-muted-foreground">({col.type})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Auto-derived column type */}
            {localColumn && (
              <div className="space-y-2">
                <Label>Column Type</Label>
                <p className="text-sm text-muted-foreground">{selectedColumnType}</p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : editingMapping ? "Update" : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Mapping</DialogTitle>
            <DialogDescription>
              Remove the mapping for{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                {deleteTarget?.hubspotField}
              </code>
              ? This field will no longer be synced from HubSpot.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
